import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of, Observable, throwError } from 'rxjs';
import { catchError, mapTo, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AppConfigService } from '../app-config.service';
import { CommonService } from '../common.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly config: any;
  private readonly ACCESS_TOKEN = 'ACCESS_TOKEN';
  private readonly REFRESH_TOKEN = 'REFRESH_TOKEN';
  private readonly SESSION_ID = 'SESSION_ID';
  private readonly VIEWER_SLIDES = 'VIEWER_SLIDES';
  private readonly URL_PARENT = 'URL_PARENT';
  private readonly MRN_ID = 'MRN_ID';
  private readonly MRN_STATUS = 'MRN_STATUS';
  private readonly MRN_SPECIMEN = 'MRN_SPECIMEN';
  private readonly PARENT_PROJECT = 'PARENT_PROJECT';
  private readonly PARENT_INFO = 'PARENT_INFO';
  private readonly LOGIN_URL = 'LOGIN_URL';
  private readonly DEFAULT_IMAGE_PREVIEW = 'DEFAULT_IMAGE_PREVIEW';
  private readonly LAST_PARENT_APP_URL = 'LAST_PARENT_APP_URL';
  private readonly VIEWER_BASE_NAME = 'VIEWER_BASE_NAME';
  private readonly CURR_USER = 'CURR_USER';

  private readonly loggedUser: string = '';

  constructor(private readonly http: HttpClient, private readonly cookieService: CookieService, private readonly router: Router, private readonly configService: AppConfigService, private readonly dialog: MatDialog,
    private readonly commonService: CommonService,) {
    this.config = configService.config;
  }

  login(user: { email: string, password: string }): Observable<string> {
    return this.http.post<any>(`${this.config.apiUrl}user/login`, user/*dataObj*/)
      .pipe(
        tap(tokens => {
          this.storeTokens(tokens);
          this.storeLoginData(tokens);
        }),
        mapTo("true"),
        catchError((error: any) => {
          if ((error.error?.status == 409) && (error.error.error == "Conflict")) {
            return of('resetPassword');
          } else if((error.error?.status == 401) && (error.error.error == "Unauthorized")) {
            return of('invalid');
          } else {
             return throwError(() => error);
          }
        }));
    // catchError(error => {
    //   return of(error.error?.status == 401 ? "invalid" : "valid");
    // }));
  }

  getTOTP() {
    let payload = { userId: this.getUserId() };
    return this.http.post<any>(`${this.config.apiUrl}user/generate-totp-qrcode`, payload);
  }

  verifyOTP(code: string) {
    let payload = {
      code,
      deviceName : "Mydevice",
      userId: this.getUserId()
    };
    return this.http.post<any>(`${this.config.apiUrl}user/verify-totp`, payload);
  }

  registerTOTP(encodedSecret: string, initialCode: string) {
    let payload = {
      encodedSecret,
      initialCode,
      deviceName : "Mydevice",
      userId: this.getUserId()
    };
    return this.http.post<any>(`${this.config.apiUrl}user/register-totp`, payload);
  }

  logout() {
    let logoutData = {
      "refreshToken": this.getRefreshToken(),
      "sessionId": this.getSessionId()
    }
    return this.http.post<any>(`${this.config.apiUrl}user/logout`, logoutData).pipe(
      tap(() => {
        this.doLogoutUser()
      }),
      mapTo(true),
      catchError(error => {
        this.doLogoutUser()
        return of(false);
      }));
  }

  logoutAPICall() {
    let logoutData = {
      "refreshToken": this.getRefreshToken(),
      "sessionId": this.getSessionId()
    }
    return this.http.post<any>(`${this.config.apiUrl}user/logout`, logoutData);
  }

  isLoggedIn() {
    return !!this.getAccessToken();
  }

  refreshToken() {
    if ((Object.keys(this.commonService.ProjectLSData).length == 0) && localStorage[this.commonService.projectName]) {
      this.commonService.ProjectLSData = JSON.parse(localStorage[this.commonService.projectName]);
    }
    this.storeAccessToken('');
    const requestData = {
      'refreshToken': this.getRefreshToken(),
      'sessionId': this.getSessionId()
    }
    return this.http.post(`${this.config.apiUrl}user/token/refresh`, requestData)
      .pipe(tap((tokens: any) => {
        this.storeTokens(tokens);
      }));
  }

  getAccessToken() {
    if ((Object.keys(this.commonService.ProjectLSData).length == 0) && localStorage[this.commonService.projectName]) {
      this.commonService.ProjectLSData = JSON.parse(localStorage[this.commonService.projectName]);
    }
    return this.commonService.ProjectLSData['accessToken'];

  }

  getPublicKey() {
    return this.http.get(`${this.config?.apiUrl}user/publicKey`);
  }

  getSessionId() {
    if ((Object.keys(this.commonService.ProjectLSData).length == 0) && localStorage[this.commonService.projectName]) {
      this.commonService.ProjectLSData = JSON.parse(localStorage[this.commonService.projectName]);
    }
    return this.commonService.ProjectLSData['sessionId'];
  }

  getUserId() {
    if (
      Object.keys(this.commonService.ProjectLSData).length == 0 &&
      localStorage[this.commonService.projectName]
    ) {
      this.commonService.ProjectLSData = JSON.parse(
        localStorage[this.commonService.projectName],
      );
    }
    return this.commonService.ProjectLSData['userId'];
  }

  isMFARegistered() {
    if (
      Object.keys(this.commonService.ProjectLSData).length == 0 &&
      localStorage[this.commonService.projectName]
    ) {
      this.commonService.ProjectLSData = JSON.parse(
        localStorage[this.commonService.projectName],
      );
    }
    return this.commonService.ProjectLSData['isMFARegistered'];
  }

  doLogoutUser() {
    this.removeTokens();
    this.clearLocalStorage();
    this.closeAllOpenDialog();
    this.commonService.sendDashboardSaveEvent(false);
    this.commonService.dashboardUserPrefChanged = false;
  }

  public storeCurrentUser(userInfo: any) {
    this.commonService.ProjectLSData['userInfo'] = userInfo;
    this.commonService.setProjectLSData();
  }

  closeAllOpenDialog() {
    if (this.dialog.openDialogs?.length > 0) {
      this.dialog.closeAll();
      return false;
    }
    return true;
  }

  getRefreshToken() {
    if ((Object.keys(this.commonService.ProjectLSData).length == 0) && localStorage[this.commonService.projectName]) {
      this.commonService.ProjectLSData = JSON.parse(localStorage[this.commonService.projectName]);
    }
    return this.commonService.ProjectLSData['refreshToken'];
  }

  resetNewUserPassword(userData: any) {
    return this.http.post(`${this.config.apiUrl}user/resetPassword`, userData)
  }

  getMRNStatus() {
    return this.cookieService.get(this.MRN_STATUS);
  }

  public storeApiUrl(apiUrl: string) {
    this.commonService.ProjectLSData['apiUrl'] = apiUrl;
    this.commonService.setProjectLSData();
  }

  private storeAccessToken(access: string) {
    this.commonService.ProjectLSData['accessToken'] = access;
    this.commonService.setProjectLSData();
  }

  private storeSessionId(sessionId: string) {
    this.commonService.ProjectLSData['sessionId'] = sessionId;
    this.commonService.setProjectLSData();
  }

  public storeViewerSlides(slidesArr: string) {
    this.commonService.ProjectLSData['ViewerSlides'] = slidesArr;
    this.commonService.setProjectLSData();
  }

  public storeParentInfo(parentInfo: any) {
    this.commonService.ProjectLSData['parentInfo'] = parentInfo;
    this.commonService.setProjectLSData();
  }

  public storeMRNID(mrnId: any) {
    this.commonService.ProjectLSData['mrnId']= mrnId;
    this.commonService.setProjectLSData();

  }

  public CurrentUser(currUser: any) {
    this.commonService.ProjectLSData['currentUser'] = currUser;
    this.commonService.setProjectLSData();
  }

  public storeMRNStatus(mrnStatus: any) {
    this.commonService.ProjectLSData['mrnStatus'] = mrnStatus;
    this.commonService.setProjectLSData();
  }

  public storeMRNSpecimen(mrnStatus: any) {
    this.commonService.ProjectLSData['mrnSpeciman'] = mrnStatus;
    this.commonService.setProjectLSData();
  }

  public storeParentProject(parentProject: any) {
    this.commonService.ProjectLSData['parentProject'] = parentProject;
    this.commonService.setProjectLSData();
  }

  public storeLastParentAppUrl(parentAppUrl: any) {
    this.commonService.ProjectLSData['parentAppUrl'] = parentAppUrl;
    this.commonService.setProjectLSData();

  }

  public storeUrlApi(urlParent: any) {
    this.commonService.ProjectLSData['urlParent'] = urlParent;
    this.commonService.setProjectLSData();
  }


  public storeDefaultImgPreview(defaultImagePreview: any) {
    this.commonService.ProjectLSData['DEFAULT_IMAGE_PREVIEW'] = defaultImagePreview;
  }
  public storeWebViewerBaseName(baseName: any) {
    this.cookieService.set(this.VIEWER_BASE_NAME, baseName, 0, '/');
  }

  private storeTokens(tokens: any) {
    if (
      !(Object.keys(this.commonService.ProjectLSData).length == 0) &&
      localStorage[this.commonService.projectName]
    ) {
      this.commonService.ProjectLSData = JSON.parse(
        localStorage[this.commonService.projectName],
      );
    }
    this.commonService.ProjectLSData['accessToken'] = tokens.accessToken;
    this.commonService.ProjectLSData['userId'] = tokens.userId;
    this.commonService.ProjectLSData['isMFARegistered'] = tokens.isMFARegistered;
    this.commonService.ProjectLSData['refreshToken'] = tokens.refreshToken;
    this.commonService.ProjectLSData['sessionId'] = tokens.sessionId;
    if (!this.commonService.setProjectLSData) {
      localStorage[this.commonService.projectName] = JSON.stringify(
        this.commonService.ProjectLSData,
      );
    } else {
      this.commonService.setProjectLSData();
    }
  }

  private storeLoginData(resp: any) {
    if ((Object.keys(this.commonService.ProjectLSData).length == 0) && localStorage[this.commonService.projectName]) {
      this.commonService.ProjectLSData = JSON.parse(localStorage[this.commonService.projectName]);
    }
    this.commonService.ProjectLSData.isfirstLogin = resp.isFirstTimeLogin ? resp.isFirstTimeLogin : false;
    this.commonService.ProjectLSData.isSessionReplaced = resp.sessionReplaced ? resp.sessionReplaced : false;
    this.commonService.ProjectLSData.chooseSession = resp.chooseSession ? resp.chooseSession : false;
    this.commonService.setProjectLSData();
  }

  removeTokens() {
    if ((Object.keys(this.commonService.ProjectLSData).length == 0) && localStorage[this.commonService.projectName]) {
      this.commonService.ProjectLSData = JSON.parse(localStorage[this.commonService.projectName]);
    }
    delete this.commonService.ProjectLSData['accessToken'];
    delete this.commonService.ProjectLSData['refreshToken'];
    delete this.commonService.ProjectLSData['sessionId'];
    this.commonService.setProjectLSData();
  }

  clearLocalStorage() {
    console.log('cleared');
    localStorage.clear();
    this.commonService.ProjectLSData = {};
    this.commonService.setProjectLSData();
  }

  activateSession() {
    return this.http.post(`${this.config.apiUrl}user/session/activate`, { sessionId: this.getSessionId() });
  }


}
